import { Collapse } from '@chakra-ui/transition';
import { ChevronDownIcon, ChevronUpIcon } from '@thebouqs/icons';
import { Box, Flex, Heading, IconButton } from '@thebouqs/ui';
import React, { useState, useMemo, useEffect } from 'react';
import {
  AccordionProps,
  AccordionItemProps,
  AccordionItemHeadingProps,
  AccordionItemPanelProps,
} from './Accordion.d';

// Accordion component
const Accordion: React.FC<AccordionProps> = ({
  children,
  allowToggle = false,
}) => {
  const validChildren = useMemo(
    () => React.Children.toArray(children).filter(React.isValidElement),
    [children]
  );

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const newIndex = validChildren.findIndex(
      child => (child as React.ReactElement<any>).props._defaultExpanded
    );

    setOpenIndex(newIndex !== -1 ? newIndex : null);
  }, [validChildren]);

  const toggleItem = (index: number) => {
    setOpenIndex(prev => (allowToggle && prev === index ? null : index));
  };

  return (
    <Box as="div" role="region">
      {validChildren.map((child, index) =>
        React.cloneElement(child as React.ReactElement, {
          isOpen: openIndex === index,
          onToggle: () => toggleItem(index),
        })
      )}
    </Box>
  );
};

// AccordionItem component
export const AccordionItem: React.FC<AccordionItemProps> = ({
  isOpen,
  onToggle,
  _defaultExpanded,
  children,
}) => {
  return (
    <Box>
      {React.Children.map(children, child =>
        React.isValidElement(child)
          ? React.cloneElement(child, { isOpen, onToggle })
          : child
      )}
    </Box>
  );
};

// AccordionItemHeading component
export const AccordionItemHeading: React.FC<AccordionItemHeadingProps> = ({
  title,
  isOpen,
  onToggle,
}) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      cursor="pointer"
      onClick={onToggle}
      marginBottom="16px">
      <Heading
        as="h3"
        fontSize="1.14rem"
        fontFamily="proxima-nova, sans-serif"
        fontWeight="600"
        letterSpacing="0.1em">
        {title}
      </Heading>
      <IconButton
        aria-label="Toggle Accordion"
        icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
        variant="ghost"
        fontSize="1.6rem"
        border="none"
      />
    </Flex>
  );
};

// AccordionItemPanel component
export const AccordionItemPanel: React.FC<AccordionItemPanelProps> = ({
  isOpen,
  children,
}) => {
  return (
    <Collapse in={isOpen} animateOpacity>
      <Box>{children}</Box>
    </Collapse>
  );
};

export default Accordion;
